export enum Role {
  ADMIN = "admin",
  MODERATOR = "moderator",
  USER = "user",
}

//#region Order Enums
// Delivery Status (string literal constants + type alias)
export const DeliveryStatus = {
  PENDING: "pending",
  PROCESSING: "processing",
  DISPATCHED: "dispatched",
  IN_TRANSIT: "in_transit",
  OUT_FOR_DELIVERY: "out_for_delivery",
  SHIPPED: "shipped",
  DELIVERED: "delivered",
  FAILED: "failed",
  RETURNED: "returned",
  PAID: "paid",
  CANCELLED: "cancelled",
  COMPLETED: "completed",
} as const;

export type DeliveryStatus =
  (typeof DeliveryStatus)[keyof typeof DeliveryStatus];

// Order Status (string literal constants + type alias)
export const ORDER_STATUS = {
  PENDING: "pending",
  PROCESSING: "processing",
  DISPATCHED: "dispatched",
  SHIPPED: "shipped",
  DELIVERED: "delivered",
  PAID: "paid",
  CANCELLED: "cancelled",
  COMPLETED: "completed",
} as const;

export type OrderStatus = (typeof ORDER_STATUS)[keyof typeof ORDER_STATUS];

export const ORDER_STATUS_VALUES = Object.values(ORDER_STATUS) as OrderStatus[];

// Optional: UI-friendly options (i18n keys/icons)
export const ORDER_STATUS_OPTIONS: {
  value: OrderStatus;
  labelKey: string;
  icon?: string;
}[] = [
  {
    value: ORDER_STATUS.PENDING,
    labelKey: "order.statuses.pending",
    icon: "🕒",
  },
  {
    value: ORDER_STATUS.PROCESSING,
    labelKey: "order.statuses.processing",
    icon: "⚙️",
  },
  {
    value: ORDER_STATUS.DISPATCHED,
    labelKey: "order.statuses.dispatched",
    icon: "📦",
  },
  {
    value: ORDER_STATUS.SHIPPED,
    labelKey: "order.statuses.shipped",
    icon: "🚚",
  },
  {
    value: ORDER_STATUS.DELIVERED,
    labelKey: "order.statuses.delivered",
    icon: "🏁",
  },
  { value: ORDER_STATUS.PAID, labelKey: "order.statuses.paid", icon: "💰" },
  {
    value: ORDER_STATUS.CANCELLED,
    labelKey: "order.statuses.cancelled",
    icon: "🚫",
  },
  {
    value: ORDER_STATUS.COMPLETED,
    labelKey: "order.statuses.completed",
    icon: "✅",
  },
];

// Order SortBy
export enum OrderSortBy {
  USERNAME = "userName",
  AMOUNT = "amount",
  STATUS = "status",
  PAYMENT_STATUS = "paymentStatus",
  CREATED_AT = "createdAt",
  UPDATED_AT = "updatedAt",
  PRODUCT_NAME = "productName",
  TRANSACTION_ID = "transactionId",
  ORDER_ID = "orderId",
}

//#endregion

//#region Paymnent Enums
// Payment Methods
export enum PaymentMethod {
  ONLINE = "online",
  OFFLINE = "offline",
  BANK_TRANSFER = "bank_transfer",
  CHEQUE = "cheque",
}
// Payment Types
export enum PaymentType {
  ORDER = "order",
  REFERRAL = "referral",
  COMMISSION = "commission",
  WITHDRAWAL = "withdrawal",
  REWARD = "reward",
  REGISTRATION = "registration",
  DONATION = "donation",
  OTHER = "other",
}
// Payment Status
export enum PaymentStatus {
  PENDING = "pending",
  SUCCESS = "success",
  FAILED = "failed",
  CANCELLED = "cancelled",
  DELETED = "deleted",
  REFUNDED = "refunded",
  PROCESSING = "processing",
  COMPLETED = "completed",
  ON_HOLD = "on_hold",
  PARTIAL = "partial",
}
// Payment Sort Options
export enum PaymentSort {
  DATE_ASC = "date_asc",
  DATE_DESC = "date_desc",
  AMOUNT_ASC = "amount_asc",
  AMOUNT_DESC = "amount_desc",
  STATUS_ASC = "status_asc",
  STATUS_DESC = "status_desc",
  TYPE_ASC = "type_asc",
  TYPE_DESC = "type_desc",
  USER_ASC = "user_asc",
  USER_DESC = "user_desc",
  METHOD_ASC = "method_asc",
  METHOD_DESC = "method_desc",
  ID_ASC = "id_asc",
  ID_DESC = "id_desc",
}
// Payment Filter Options
export enum PaymentFilter {
  ALL = "all",
  PENDING = "pending",
  SUCCESS = "success",
  FAILED = "failed",
  CANCELLED = "cancelled",
  DELETED = "deleted",
  REFUNDED = "refunded",
  PROCESSING = "processing",
  COMPLETED = "completed",
  ON_HOLD = "on_hold",
  PARTIAL = "partial",
  ORDER = "order",
  REFERRAL = "referral",
  COMMISSION = "commission",
  WITHDRAWAL = "withdrawal",
  REWARD = "reward",
  REGISTRATION = "registration",
  DONATION = "donation",
  OTHER = "other",
}
// Payment Date Range
export enum PaymentDateRange {
  TODAY = "today",
  YESTERDAY = "yesterday",
  LAST_7_DAYS = "last_7_days",
  LAST_30_DAYS = "last_30_days",
  LAST_90_DAYS = "last_90_days",
  LAST_180_DAYS = "last_180_days",
  LAST_365_DAYS = "last_365_days",
  THIS_MONTH = "this_month",
  LAST_MONTH = "last_month",
  THIS_YEAR = "this_year",
  LAST_YEAR = "last_year",
  CUSTOM = "custom",
}
// Payment Sort Order
export enum PaymentSortOrder {
  ASC = "asc",
  DESC = "desc",
}

//#endregion

// User Status
export enum UserStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  SUSPENDED = "SUSPENDED",
}

// Login Methods
export enum LoginMethod {
  EMAIL = "email",
  PHONE = "phone",
  AADHAR = "aadhar",
}

// Navigation Roles
export enum NavigationRole {
  ADMIN = "admin",
  MODERATOR = "moderator",
  USER = "user",
}

// Modal Types
export enum ModalType {
  ADD = "add",
  EDIT = "edit",
  DELETE = "delete",
  VIEW = "view",
}

// Table Actions
export enum TableAction {
  VIEW = "view",
  EDIT = "edit",
  DELETE = "delete",
  ACTIVATE = "activate",
  DEACTIVATE = "deactivate",
}

//#region Product Enums
// Product Status
export enum ProductStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  ARCHIVED = "ARCHIVED",
}
// Product Categories
export enum ProductCategory {
  ELECTRONICS = "electronics",
  FASHION = "fashion",
  HOME = "home",
  BEAUTY = "beauty",
  SPORTS = "sports",
  TOYS = "toys",
  BOOKS = "books",
  AUTOMOTIVE = "automotive",
}
// Product Sort Options
export enum ProductSort {
  NAME_ASC = "name_asc",
  NAME_DESC = "name_desc",
  PRICE_ASC = "price_asc",
  PRICE_DESC = "price_desc",
  RATING_ASC = "rating_asc",
  RATING_DESC = "rating_desc",
  NEWEST = "newest",
  OLDEST = "oldest",
}
// Product Price Ranges
export enum ProductPriceRange {
  UNDER_100 = "under_100",
  FROM_100_TO_500 = "from_100_to_500",
  FROM_500_TO_1000 = "from_500_to_1000",
  FROM_1000_TO_5000 = "from_1000_to_5000",
  ABOVE_5000 = "above_5000",
}
// Product Commission Types
export enum ProductCommissionType {
  FIXED = "fixed",
  PERCENTAGE = "percentage",
  NONE = "none",
}

export enum BulkProductAction {
  DELETE = "delete",
  ACTIVATE = "activate",
  DEACTIVATE = "deactivate",
  UPDATE_CATEGORY = "update_category",
  UPDATE_PRICE = "update_price",
}
export enum ProductImageType {
  SINGLE = "single",
  MULTIPLE = "multiple",
}

//#endregion

//#region Commission Enums
// Commission Status
export enum CommissionStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  PAID = "PAID",
}

// Commission Types
export enum CommissionType {
  ALL = "ALL",
  PAID = "PAID",
  PENDING = "PENDING",
  REFERRAL = "REFERRAL",
  PRODUCT_ORDER = "PRODUCT_ORDER",
}

export enum CommissionPeriod {
  TODAY = "TODAY",
  WEEK = "WEEK",
  MONTH = "MONTH",
  QUARTER = "QUARTER",
  YEAR = "YEAR",
  CUSTOM = "CUSTOM",
}

//#endregion

//#region Sort Enums
// Sort By Options
export enum SortBy {
  AMOUNT = "amount",
  DATE = "date",
  LEVEL = "level",
}
// Sort Order Options
export enum SortOrder {
  ASC = "asc",
  DESC = "desc",
}
//#endregion

//#region Nominee Enums
export enum NomineeRelation {
  SPOUSE = "Spouse",
  PARENT = "Parent",
  SIBLING = "Sibling",
  CHILD = "Child",
  FRIEND = "Friend",
  OTHER = "Other",
}
// Nominee Status
export enum NomineeStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  PENDING = "PENDING",
  REJECTED = "REJECTED",
}
//#endregion

//#region common Enums
// Common Status
export enum DialogVariant {
  DANGER = "danger",
  WARNING = "warning",
  INFO = "info",
  SUCCESS = "success",
}
